import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Rating,
  TextField,
  Alert
} from '@mui/material';
import { Star, ThumbUp } from '@mui/icons-material';
import { db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

/**
 * Smart Review Prompt Component
 * 
 * Shows review prompts at optimal times:
 * - After 7 days of active use
 * - After completing 20 tasks
 * - After upgrading to premium
 * - Never more than once every 30 days
 * 
 * Usage: <ReviewPrompt />
 */
function ReviewPrompt() {
  const { currentUser, userProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [step, setStep] = useState(1); // 1 = rating, 2 = review text, 3 = where to post
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkIfShouldPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userProfile]);

  const checkIfShouldPrompt = async () => {
    // Skip for child accounts (no Firebase Auth session) and unauthenticated users
    if (!currentUser || !userProfile || userProfile.role === 'child') return;

    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data();

      // Check if already reviewed or dismissed recently
      if (userData?.hasLeftReview) return;
      if (userData?.reviewPromptDismissedAt) {
        const dismissedDate = new Date(userData.reviewPromptDismissedAt);
        const daysSinceDismiss = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < 30) return; // Don't prompt for 30 days after dismiss
      }

      // Check if already prompted in this session
      const lastPromptSession = sessionStorage.getItem('reviewPromptShown');
      if (lastPromptSession) return;

      // Calculate if user qualifies for prompt
      const accountAge = userData?.createdAt 
        ? (Date.now() - new Date(userData.createdAt).getTime()) / (1000 * 60 * 60 * 24)
        : 0;

      const totalAllowance = userData?.totalAllowanceEarned || 0;
      const tasksCompleted = userData?.tasksCompleted || 0;

      // Trigger conditions (any one is enough)
      const qualifies = 
        accountAge >= 7 ||           // 7 days active
        tasksCompleted >= 20 ||      // 20 tasks completed
        totalAllowance >= 50 ||      // Earned $50+
        userData?.subscriptionTier === 'premium'; // Upgraded to premium

      if (qualifies) {
        // Wait a bit before showing (not immediately on page load)
        setTimeout(() => {
          setOpen(true);
          sessionStorage.setItem('reviewPromptShown', 'true');
        }, 5000); // 5 seconds delay
      }
    } catch (error) {
      console.error('Error checking review prompt eligibility:', error);
    }
  };

  const handleDismiss = async () => {
    setOpen(false);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        reviewPromptDismissedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error dismissing review prompt:', error);
    }
  };

  const handleRatingChange = (newRating) => {
    setRating(newRating);
    if (newRating >= 4) {
      // High rating - proceed to review text
      setStep(2);
    } else {
      // Low rating - send to feedback instead
      setOpen(false);
      // You could open feedback widget here
      alert('We\'re sorry you\'re not having a great experience. Please send us feedback so we can improve!');
    }
  };

  const handleSubmitReview = async () => {
    setSubmitting(true);
    try {
      // Store review in database
      await updateDoc(doc(db, 'users', currentUser.uid), {
        hasLeftReview: true,
        reviewRating: rating,
        reviewText: review,
        reviewedAt: new Date().toISOString()
      });

      // Move to final step (where to post)
      setStep(3);
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleShareReview = (platform) => {
    // For now, just open a generic thank you
    // In the future, deep link to Play Store, App Store, etc.
    const platforms = {
      google: 'https://earn-echore.app/feedback', // Will update with Google Business link once app is live
      trustpilot: 'https://earn-echore.app/feedback', // Will update when Trustpilot profile created
      producthunt: 'https://earn-echore.app/feedback' // Will update when ProductHunt submitted
    };

    if (platform === 'later') {
      setOpen(false);
      return;
    }

    window.open(platforms[platform] || '#', '_blank');
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleDismiss} maxWidth="xs" fullWidth>
      {step === 1 && (
        <>
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              Enjoying Earn-E? 🤖
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your feedback helps us improve!
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                How would you rate your experience?
              </Typography>
              <Rating
                name="user-rating"
                value={rating}
                onChange={(event, newValue) => handleRatingChange(newValue)}
                size="large"
                sx={{ fontSize: '3rem', my: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button onClick={handleDismiss} size="small">
              Maybe Later
            </Button>
          </DialogActions>
        </>
      )}

      {step === 2 && (
        <>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Star sx={{ color: 'warning.main' }} />
              <Typography variant="h6">
                Awesome! {rating === 5 ? '🎉' : ''}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="success" sx={{ fontSize: '0.85rem' }}>
                <strong>Thank you!</strong> Your {rating}-star rating means a lot to us.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Would you like to share what you love about Earn-E?
              </Typography>
              <TextField
                label="Your Review (optional)"
                value={review}
                onChange={(e) => setReview(e.target.value)}
                multiline
                rows={4}
                fullWidth
                placeholder="What do you love most about Earn-E Chore Helper?"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDismiss}>Skip</Button>
            <Button
              onClick={handleSubmitReview}
              variant="contained"
              disabled={submitting}
              startIcon={<ThumbUp />}
            >
              {submitting ? 'Saving...' : 'Continue'}
            </Button>
          </DialogActions>
        </>
      )}

      {step === 3 && (
        <>
          <DialogTitle sx={{ textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Thank You! 🙏
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Your feedback has been saved. Would you like to share your review publicly?
              </Typography>
              <Alert severity="info" sx={{ fontSize: '0.75rem', textAlign: 'left' }}>
                Public reviews help other families discover Earn-E! Plus, you'll get a special "Early Supporter" badge! 🏆
              </Alert>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleShareReview('google')}
                >
                  Share on Google
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleShareReview('trustpilot')}
                >
                  Share on Trustpilot
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleShareReview('producthunt')}
                >
                  Share on Product Hunt
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button onClick={() => handleShareReview('later')} size="small">
              Maybe Later
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default ReviewPrompt;


